import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../components/design-system/primitives/Button/Button.js';
import { Card } from '../components/design-system/primitives/Card/Card.js';
import { SEO } from '../components/seo/index.js';
import { selectNeedsEmailVerification, useAuthStore } from '../store/authStore.js';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const needsVerification = useAuthStore(selectNeedsEmailVerification);
  const resendVerificationEmail = useAuthStore((s) => s.resendVerificationEmail);
  const reloadAndSync = useAuthStore((s) => s.reloadAndSync);
  const signOut = useAuthStore((s) => s.signOut);

  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [notYet, setNotYet] = useState(false);

  // Not signed in → back to login. Already verified → into the app.
  useEffect(() => {
    if (!isInitialized) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!needsVerification) {
      navigate('/welcome', { replace: true });
    }
  }, [isInitialized, user, needsVerification, navigate]);

  const checkNow = useCallback(
    async (silent: boolean) => {
      if (!silent) setChecking(true);
      try {
        const verified = await reloadAndSync();
        if (verified) {
          navigate('/welcome', { replace: true });
        } else if (!silent) {
          setNotYet(true);
        }
      } catch {
        /* transient — leave the user on the page */
      } finally {
        if (!silent) setChecking(false);
      }
    },
    [reloadAndSync, navigate],
  );

  // Gently poll so the page advances on its own once the link is clicked.
  useEffect(() => {
    const id = window.setInterval(() => void checkNow(true), 6000);
    return () => window.clearInterval(id);
  }, [checkNow]);

  const onResend = async () => {
    setResending(true);
    setNotYet(false);
    try {
      await resendVerificationEmail();
      toast.success(t('auth.verifyEmail.resent'));
    } catch {
      toast.error(t('auth.verifyEmail.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <SEO
        title={t('auth.verifyEmail.title', { defaultValue: 'Activate your account' })}
        description={t('auth.verifyEmail.metaDescription', {
          defaultValue: 'Confirm your email address to activate your InterviewGator account.',
        })}
      />
      <Card variant="outlined" className="w-full rounded-lg !border-0 bg-ig-bg-primary p-6 md:p-8">
        <div className="mb-6">
          <p className="mb-3 font-sans text-[11px] uppercase tracking-[0.136em] text-ig-accent">
            {t('auth.verifyEmail.eyebrow')}
          </p>
          <h1 className="font-display text-[32px] font-normal leading-tight text-ig-text-primary">
            {t('auth.verifyEmail.title')}
          </h1>
          <p className="mt-4 text-[14px] leading-relaxed text-ig-text-secondary">
            {t('auth.verifyEmail.body')}
          </p>
          {user?.email ? (
            <p className="mt-3 rounded-md bg-ig-bg-tertiary px-3 py-2 text-center font-mono text-[14px] text-ig-text-primary">
              {user.email}
            </p>
          ) : null}
        </div>

        {notYet ? (
          <p className="mb-4 rounded-md border border-ig-warning/40 bg-ig-warning/10 px-3 py-2 text-[13px] text-ig-warning">
            {t('auth.verifyEmail.notYet')}
          </p>
        ) : null}

        <div className="space-y-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            loading={checking}
            onClick={() => void checkNow(false)}
            className="w-full rounded-md"
          >
            {t('auth.verifyEmail.checkNow')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            loading={resending}
            onClick={() => void onResend()}
            className="w-full rounded-md !border-0"
          >
            {t('auth.verifyEmail.resend')}
          </Button>
        </div>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}
            className="text-[13px] text-ig-text-tertiary hover:text-ig-text-primary hover:underline"
          >
            {t('auth.verifyEmail.useAnotherAccount')}
          </button>
        </div>
      </Card>
    </>
  );
}
